import React, { useEffect, useState, useRef } from "react";
import { Quote } from "lucide-react";
import api from "@/lib/api";
import ReviewForm from "@/components/ReviewForm";
import { StarRow, serviceLabel, formatLocation, SERVICE_OPTIONS } from "@/components/review-utils";

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "long", year: "numeric" });
  } catch {
    return "";
  }
}

export default function Reviews() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const formRef = useRef(null);

  const load = () => {
    setLoading(true);
    api.get("/reviews", { params: { limit: 100 } })
      .then(({ data }) => setReviews(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const filtered = filter === "all" ? reviews : reviews.filter((r) => r.service === filter);

  const avg = reviews.length
    ? (reviews.reduce((a, r) => a + r.rating, 0) / reviews.length).toFixed(1)
    : null;

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div data-testid="reviews-page" className="pt-32 pb-32">
      {/* HERO */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-8">
            <div className="overline mb-6">— Client Stories</div>
            <h1 className="font-serif text-5xl md:text-7xl lg:text-[7rem] text-white tracking-tighter leading-[0.95]">
              Stories of <span className="italic text-[#D4AF37]">protection.</span>
              <br />
              Stories of <span className="italic text-[#D4AF37]">purpose.</span>
            </h1>
          </div>
          <div className="lg:col-span-4 lg:pl-8">
            {avg && (
              <div className="mb-6">
                <div className="overline mb-2">Overall Rating</div>
                <div className="flex items-baseline gap-4">
                  <span className="font-serif text-6xl text-white">{avg}</span>
                  <StarRow value={Math.round(parseFloat(avg))} size={18} />
                </div>
                <div className="text-xs text-white/40 mt-2 tracking-wide">from {reviews.length} verified review{reviews.length === 1 ? "" : "s"}</div>
              </div>
            )}
            <button
              data-testid="reviews-cta-leave"
              onClick={scrollToForm}
              className="btn-primary"
            >
              Leave a Review
            </button>
          </div>
        </div>
      </section>

      {/* FILTERS + LIST */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
        <div className="flex flex-wrap items-center gap-2 mb-10">
          {[{ value: "all", label: "All Services" }, ...SERVICE_OPTIONS.filter((s) => s.value !== "general")].map((t) => (
            <button
              key={t.value}
              data-testid={`reviews-filter-${t.value}`}
              onClick={() => setFilter(t.value)}
              className={`px-5 py-2.5 text-[11px] tracking-[0.22em] uppercase border transition-all ${
                filter === t.value
                  ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/5"
                  : "border-white/10 text-white/55 hover:border-white/30 hover:text-white"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {loading && (
          <div data-testid="reviews-loading" className="card-luxury p-16 text-center text-white/50">
            Loading stories...
          </div>
        )}

        {!loading && reviews.length === 0 && (
          <div data-testid="reviews-empty" className="card-luxury p-16 text-center">
            <div className="overline mb-4">— No Stories Yet</div>
            <h3 className="font-serif text-3xl text-white mb-3">Be the first to share yours.</h3>
            <p className="text-white/55 max-w-md mx-auto mb-8">
              We're just getting started gathering stories from the families and agents we serve.
            </p>
            <button data-testid="reviews-empty-cta" onClick={scrollToForm} className="btn-secondary">
              Leave the First Review
            </button>
          </div>
        )}

        {!loading && reviews.length > 0 && filtered.length === 0 && (
          <div data-testid="reviews-no-match" className="card-luxury p-12 text-center text-white/55">
            No reviews match this filter yet. Try a different service.
          </div>
        )}

        {filtered.length > 0 && (
          <div data-testid="reviews-list" className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {filtered.map((r) => (
              <article key={r.id} data-testid={`review-${r.id}`} className="card-luxury p-8 md:p-10 relative flex flex-col">
                <Quote className="absolute top-6 right-6 w-10 h-10 text-[#D4AF37]/15" strokeWidth={1} />
                <StarRow value={r.rating} className="mb-5" />
                <p className="font-serif text-xl md:text-2xl text-white leading-snug mb-6 flex-1">"{r.text}"</p>
                <div className="flex items-center justify-between pt-5 border-t border-white/5">
                  <div>
                    <div className="text-white font-medium">{r.name}</div>
                    <div className="text-xs text-white/40 tracking-wide">
                      {[formatLocation(r), fmtDate(r.created_at)].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                  <div className="text-[10px] tracking-[0.22em] uppercase text-[#D4AF37]">{serviceLabel(r.service)}</div>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      {/* LEAVE A REVIEW */}
      <section ref={formRef} className="max-w-3xl mx-auto px-6 md:px-10 mt-32">
        <div className="overline mb-5">— Share Your Story</div>
        <h2 className="font-serif text-4xl md:text-5xl text-white tracking-tight leading-tight mb-4">
          Leave a <span className="italic text-[#D4AF37]">review.</span>
        </h2>
        <p className="text-white/65 mb-10 leading-relaxed">
          Your story helps other families feel confident about taking the next step. Reviews are briefly checked before publishing.
        </p>
        <ReviewForm onSubmitted={load} />
      </section>
    </div>
  );
}
